import React, { FC, memo, useCallback, useMemo, useState } from 'react';
import { DisciplineLiveStatus } from '@om-fe/models';
import LiveMatchListSelector from '../../components/match-list/containers/live-match-list/live-match-list-selector';
import getConfig, { OMFE2ConfigModel } from '@om-fe/core/utils/config';
import { SportsNavModel, UseSportsNavStore } from '@om-fe/widgets/dist/widgets/navs/sports-nav/sports-nav.hooks';
import { GeneralIcon } from '@om-fe/widgets/dist/components/icon/general-icon';
import { getLiveEventSidebarMatchListConfig } from '@om-fe/core/components/match-details/utils/match-details.utils';
import { BemMethod, createBem, isBrowser } from '@om-fe/utils';
import { BreakpointsState } from '@om-fe/core/core/store/breakpoints';
import { useItem } from '@om-fe/store';
import { getMainMarketsLimit, getMainMarketsLimitByWidth } from '@om-fe/core/components/match-list/utils/match-list.utils';
import { DisciplineIcon } from '@om-fe/widgets/dist/components/icon/discipline-icon';
import { MAIN_MARKET_MIN_LIMIT } from '@om-fe/core/components/match-list/utils/match-list.constants';
import { DEFAULT_DISPLAYED_NUMBER_OF_MATCHES } from '../match-list/utils/match-list.constants';

interface Props {
    readonly breakpoints: BreakpointsState | null
    readonly sportId: string;
    readonly useStore: UseSportsNavStore;
    readonly initialIsExpanded?: boolean;
    readonly instanceId: number;
    readonly activeMatchId: string | null;
    readonly isLiveSports: boolean;
    readonly showOnlyTournamentHeader: boolean;
    readonly isAuthenForTopic?: boolean;
}

const bem: BemMethod = createBem('MatchesContainer');

const MatchListAllItem: FC<Props> = memo(function MatchListAllItemComponent(
    {
        sportId,
        useStore,
        initialIsExpanded = false,
        instanceId,
        activeMatchId,
        isLiveSports,
        breakpoints,
        showOnlyTournamentHeader,
        isAuthenForTopic,
    }): React.ReactNode {
    const [isExpanded, setIsExpanded] = useState<boolean>(initialIsExpanded);
    const item: SportsNavModel = useItem<SportsNavModel>(useStore, sportId);
    const { name, liveCounter } = item;

    const toggleIsExpanded = useCallback(() => {
        setIsExpanded(value => !value);
    }, []);

    const config: OMFE2ConfigModel = getConfig();
    const matchListLayout = config.matchListConfig?.commonConfig?.matchListItemLayout;
    const sidebarMatchlistConfig= getLiveEventSidebarMatchListConfig();
    const numberOfOddsExpanded = !isLiveSports ? sidebarMatchlistConfig.numberOfOddsExpanded : undefined;
    const eventsNumber: number = config.matchListConfig?.commonConfig?.eventsNumber ?? DEFAULT_DISPLAYED_NUMBER_OF_MATCHES;
    const mainMarketsLimit: number = config.mainMarketsLimit?.responsive
        ? config.mainMarketsLimit?.byWidth && (isBrowser() && window)
            ? getMainMarketsLimitByWidth(window.innerWidth, config.mainMarketsLimit?.breakpointsByWidth)
            : getMainMarketsLimit(breakpoints)
        : MAIN_MARKET_MIN_LIMIT;
    const marketSelector: boolean = config?.matchListConfig?.matchListSelector?.enabled ?? false;
    const marketGroupsOverviewNumber: number | undefined = config?.matchListConfig?.matchListSelector?.numberOfList;

    const marketsHeaderOptions = useMemo(() => {
        const configGeneral: OMFE2ConfigModel = getConfig();

        return showOnlyTournamentHeader ? 'show_tournament' : configGeneral.marketsHeaderOptions;
    }, [showOnlyTournamentHeader])

    return (
        <div className={bem()}>
            <div className={bem('Header')}>
                <button className={bem('CollapseButton')} onClick={toggleIsExpanded}>
                    <DisciplineIcon disciplineId={sportId} />
                    <span className={bem('Title')}>{name}</span>
                    {liveCounter !== undefined && (<span className={bem('Counter')} >{liveCounter}</span>)}
                    <GeneralIcon name={isExpanded ? 'arr-up' : 'arr-down'} />
                </button>
            </div>
            {isExpanded &&
                <div>
                    <LiveMatchListSelector
                        activeMatchId={activeMatchId}
                        numberOfOddsExpanded={numberOfOddsExpanded}
                        disciplineId={sportId}
                        instanceId={`LeftMenu-LiveUpcomingTabs-${DisciplineLiveStatus.LIVE}-${instanceId}`}
                        limitNumberOfEvents={eventsNumber}
                        liveStatus={DisciplineLiveStatus.LIVE}
                        mainMarketsLimit={mainMarketsLimit}
                        marketsHeaderOptions={marketsHeaderOptions}
                        hasMarketSelector={marketSelector}
                        marketGroupsOverviewNumber={marketGroupsOverviewNumber}
                        itemLayout={matchListLayout}
                        showMatchListHeader={false}
                        showMarketInMatchListGroupHeader={true}
                        isAuthenForTopic={isAuthenForTopic}
                    />
                </div>
            }
        </div>
    )
});

export default MatchListAllItem;
